interface TherapeuticSexualWellnessGuardrailConfig {
  therapeuticFrameworks: {
    traumaInformed: boolean;
    shameReduction: boolean;
    empowermentFocus: boolean;
    bodyAutonomy: boolean;
  };
  languageGuidelines: {
    affirmingTone: boolean;
    nonPathologizing: boolean;
    strengthsBased: boolean;
    clientCentered: boolean;
  };
  safetyProtocols: {
    crisisIntervention: boolean;
    traumaResponseAwareness: boolean;
    consentEmphasized: boolean;
    boundariesRespected: boolean;
  };
  clinicalConsiderations: {
    libidoNormalization: boolean;
    pleasurePositive: boolean;
    relationshipContext: boolean;
    healingOriented: boolean;
  };
}

export const THERAPEUTIC_SEXUAL_WELLNESS_GUARDRAILS: TherapeuticSexualWellnessGuardrailConfig = {
  therapeuticFrameworks: {
    traumaInformed: true,
    shameReduction: true,
    empowermentFocus: true,
    bodyAutonomy: true
  },
  languageGuidelines: {
    affirmingTone: true,
    nonPathologizing: true,
    strengthsBased: true,
    clientCentered: true
  },
  safetyProtocols: {
    crisisIntervention: true,
    traumaResponseAwareness: true,
    consentEmphasized: true,
    boundariesRespected: true
  },
  clinicalConsiderations: {
    libidoNormalization: true,
    pleasurePositive: true,
    relationshipContext: true,
    healingOriented: true
  }
};

export const validateTherapeuticSexualWellnessContent = (content: string): { isValid: boolean; violations: string[]; corrections: string[] } => {
  const violations: string[] = [];
  const corrections: string[] = [];

  // Check for shame-based language
  const shameWords = ['abnormal', 'wrong', 'broken', 'dysfunction', 'failure', 'inadequate'];
  const lowerContent = content.toLowerCase();
  
  shameWords.forEach(word => {
    if (lowerContent.includes(word)) {
      violations.push(`Shame-based language detected: "${word}"`);
      corrections.push('Use affirming, strength-based language instead');
    }
  });

  // Ensure trauma-informed approach
  if (!lowerContent.includes('safe') && !lowerContent.includes('comfort') && !lowerContent.includes('pace')) {
    violations.push('Missing trauma-informed safety language');
    corrections.push('Include references to safety, comfort, and personal pacing');
  }

  // Check for empowerment language
  const empowermentWords = ['choice', 'agency', 'power', 'strength', 'wisdom', 'healing'];
  const hasEmpowerment = empowermentWords.some(word => lowerContent.includes(word));
  
  if (!hasEmpowerment) {
    violations.push('Missing empowerment-focused language');
    corrections.push('Include language that emphasizes personal agency and inner strength');
  }

  // Ensure pleasure-positive approach
  if (lowerContent.includes('pleasure') || lowerContent.includes('libido') || lowerContent.includes('sexual')) {
    if (!lowerContent.includes('natural') && !lowerContent.includes('normal') && !lowerContent.includes('healthy')) {
      violations.push('Missing normalizing language for sexuality');
      corrections.push('Include language that normalizes sexual health and pleasure');
    }
  }

  return {
    isValid: violations.length === 0,
    violations,
    corrections
  };
};

export const createTherapeuticSexualWellnessSafetyPrompt = (): string => {
  return `
CRITICAL THERAPEUTIC SEXUAL WELLNESS GUIDELINES:
- Use trauma-informed, shame-free language throughout
- Normalize sexual desire, pleasure, and diverse experiences
- Emphasize personal agency, choice, and empowerment
- Respect boundaries and individual pacing
- Include safety and consent considerations
- Focus on healing and growth rather than fixing or pathology
- Honor the therapeutic relationship and Michele's clinical expertise
- Include grounding and self-compassion elements
- Avoid prescriptive language - focus on exploration and discovery
- Acknowledge that healing is a process and happens at individual pace
`;
};

export const isTherapeuticSexualWellnessContentSafe = (content: string): boolean => {
  const validation = validateTherapeuticSexualWellnessContent(content);
  return validation.isValid;
};